/** @jsxImportSource theme-ui */
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Flex, Spinner, PlaceholderMessage } from '@infotrack/zenith-ui';
import { StaffHeader } from '../components/staffHeader';
import { StaffCard } from '../components/staffCard';
import { useStaffList } from '../hooks';
import {
  pageStyles,
  alphabetPanelStyles,
  alphabetLetterStyles,
  alphabetLetterActiveStyles,
  contentAreaStyles,
  headerContentStyles,
  listContainerStyles,
  emptyStyles,
} from './staffPage.styles';

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

/**
 * Staff management page with alphabet filter
 * Displays staff directory with search, filtering, and alphabet navigation
 */
export const StaffPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [selectedLetter, setSelectedLetter] = useState<string | null>(null);

  // Fetch staff list from API
  const {
    staffList,
    hasAdminAccess,
    hasEditorAccess,
    isLoading,
    isError,
  } = useStaffList();

  // Allow add staff if user has admin or editor access
  const canAddStaff = hasAdminAccess || hasEditorAccess;

  // Filter staff based on search, inactive filter, and alphabet
  const filteredStaff = useMemo(() => {
    let filtered = staffList;

    // Filter by active status
    if (!includeInactive) {
      filtered = filtered.filter(s => s.isActive);
    }

    // Filter by alphabet letter
    if (selectedLetter) {
      filtered = filtered.filter(s =>
        s.fullName?.toUpperCase().startsWith(selectedLetter)
      );
    }

    // Filter by search text
    if (searchText.trim()) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter(
        s =>
          s.fullName?.toLowerCase().includes(search) ||
          s.staffEmail?.toLowerCase().includes(search)
      );
    }

    // Sort by full name
    return filtered.sort((a, b) => (a.fullName || '').localeCompare(b.fullName || ''));
  }, [staffList, searchText, includeInactive, selectedLetter]);

  const handleLetterClick = (letter: string) => {
    if (selectedLetter === letter) {
      setSelectedLetter(null); // Deselect if clicking the same letter
    } else {
      setSelectedLetter(letter);
    }
  };

  const handleStaffClick = (id: number) => {
    navigate(`/staff/${id}`);
  };

  const handleAddStaff = () => {
    navigate('/staff/new');
  };

  const handleDownloadCSV = () => {
    // CSV headers matching the required fields
    const headers = [
      'staffId',
      'firstName',
      'surname',
      'employeeId',
      'staffEmail',
      'managerName',
      'managerEmail',
      'managerId',
      'executiveId',
      'department',
      'company',
      'isActive',
      'isManager',
      'isExecutive',
      'username',
      'cadence',
      'fullName'
    ];

    // Convert staff data to CSV format
    const csvRows = [
      headers.join(','),
      ...filteredStaff.map(staff => {
        // Split fullName into firstName and surname (mock implementation)
        const nameParts = (staff.fullName || '').split(' ');
        const firstName = nameParts[0] || '';
        const surname = nameParts.slice(1).join(' ') || '';
        
        return [
          staff.staffId,
          `"${firstName}"`,
          `"${surname}"`,
          `"${staff.employeeId || ''}"`,
          `"${staff.staffEmail || ''}"`,
          `"${staff.managerName || ''}"`,
          `"${staff.managerEmail || ''}"`,
          staff.managerId || '',
          staff.executiveId || '',
          `"${staff.department || ''}"`,
          `"${staff.company || ''}"`,
          staff.isActive,
          staff.isManager,
          staff.isExecutive,
          '', // username - not in mock data
          '', // cadence - not in mock data
          `"${staff.fullName || ''}"`
        ].join(',');
      })
    ];

    // Create blob and download
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `staff-list-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (isLoading) {
    return (
      <Box sx={pageStyles}>
        <Spinner fullSize aria-label="Loading staff data" />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={pageStyles}>
        <PlaceholderMessage
          title="Error loading staff data"
          subtitle="Please try again later or contact support if the problem persists."
        />
      </Box>
    );
  }

  return (
    <Flex sx={pageStyles}>
      {/* Alphabet Filter Panel */}
      <Box sx={alphabetPanelStyles}>
        {ALPHABET.map(letter => (
          <Box
            key={letter}
            sx={{
              ...alphabetLetterStyles,
              ...(selectedLetter === letter ? alphabetLetterActiveStyles : {}),
            }}
            onClick={() => handleLetterClick(letter)}
            role="button"
            aria-label={`Filter by letter ${letter}`}
            aria-pressed={selectedLetter === letter}
          >
            {letter}
          </Box>
        ))}
      </Box>

      {/* Main Content Area */}
      <Flex sx={contentAreaStyles}>
        <StaffHeader
          resultCount={filteredStaff.length}
          searchText={searchText}
          onSearchChange={setSearchText}
          onSearchClear={() => setSearchText('')}
          includeInactive={includeInactive}
          onIncludeInactiveChange={setIncludeInactive}
          hasAdminAccess={canAddStaff}
          onAddStaff={handleAddStaff}
          onDownloadCSV={handleDownloadCSV}
        />

        <Box sx={headerContentStyles}>
          {filteredStaff.length === 0 ? (
            <Box sx={emptyStyles}>
              <PlaceholderMessage
                title="No staff members found"
                subtitle={
                  searchText || selectedLetter
                    ? 'Try adjusting your search or filter criteria'
                    : 'No staff members available'
                }
              />
            </Box>
          ) : (
            <Box sx={listContainerStyles}>
              {filteredStaff.map(staff => (
                <StaffCard
                  key={staff.staffId}
                  staff={staff}
                  onClick={handleStaffClick}
                />
              ))}
            </Box>
          )}
        </Box>
      </Flex>
    </Flex>
  );
};
